import { useCallback, useEffect, useRef, useState } from "react";

import { apiGetPurchaseDefaults } from "api/coverage/purchaseDefaults";
import type { ProductPurchaseDefaults, VendorCatalogEntry } from "api/types";

type State = {
  byCode: Record<string, ProductPurchaseDefaults>;
  vendorCatalog: VendorCatalogEntry[];
  isLoading: boolean;
  datamartAvailable: boolean;
  datamartError: string;
};

const INITIAL: State = {
  byCode: {},
  vendorCatalog: [],
  isLoading: false,
  datamartAvailable: true,
  datamartError: "",
};

/**
 * Defaults de compra (proveedor, almacén, costo) para los códigos indicados.
 *
 * Cachea por código en un ref: al abrir y cerrar el panel de una orden varias
 * veces no tiene sentido volver a pegarle al datamart por los mismos productos.
 * Sólo pide los que faltan.
 */
export function usePurchaseDefaults(enabled: boolean, productCodes: string[]) {
  const [state, setState] = useState<State>(INITIAL);
  const cacheRef = useRef<Record<string, ProductPurchaseDefaults>>({});
  const inFlightRef = useRef<Set<string>>(new Set());

  // Clave estable: sin esto el efecto se dispara en cada render, porque el
  // array de códigos se reconstruye arriba en cada pasada.
  const codesKey = Array.from(
    new Set(productCodes.map((c) => String(c ?? "").trim()).filter(Boolean)),
  )
    .sort()
    .join(",");

  const fetchMissing = useCallback(async () => {
    const codes = codesKey ? codesKey.split(",") : [];
    const missing = codes.filter(
      (code) => !cacheRef.current[code] && !inFlightRef.current.has(code),
    );
    if (missing.length === 0) {
      setState((prev) => ({ ...prev, byCode: { ...cacheRef.current } }));
      return;
    }
    missing.forEach((code) => inFlightRef.current.add(code));
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const result = await apiGetPurchaseDefaults(missing);
      cacheRef.current = { ...cacheRef.current, ...result.byCode };
      setState((prev) => ({
        byCode: { ...cacheRef.current },
        // El catálogo es global; una tanda que no lo traiga no debe vaciarlo.
        vendorCatalog:
          result.vendorCatalog.length > 0
            ? result.vendorCatalog
            : prev.vendorCatalog,
        isLoading: false,
        datamartAvailable: result.datamartAvailable,
        datamartError: result.datamartError,
      }));
    } catch (err) {
      // Un fallo aquí no debe romper el editor: el usuario puede cargar los
      // datos a mano. Se avisa y se deja seguir.
      setState((prev) => ({
        ...prev,
        isLoading: false,
        datamartAvailable: false,
        datamartError:
          "No se pudieron cargar los datos de compra sugeridos.",
      }));
    } finally {
      missing.forEach((code) => inFlightRef.current.delete(code));
    }
  }, [codesKey]);

  useEffect(() => {
    if (!enabled || !codesKey) {
      return;
    }
    void fetchMissing();
  }, [enabled, codesKey, fetchMissing]);

  return state;
}
